import React from 'react';
import { FaInstagram, FaWhatsapp, FaArrowUp } from 'react-icons/fa';
import { getRandomWhatsappNumber } from '../../utils';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <footer className="text-white pt-0 pb-8 relative" style={{ backgroundColor: '#002244' }}>
      {/* SVG de transição */}
      <div className="section-divider">
        <svg 
          viewBox="0 0 1920 200" 
          xmlns="http://www.w3.org/2000/svg" 
          preserveAspectRatio="none"
          style={{
            width: '100%',
            height: 'auto',
            display: 'block'
          }}
        >
          <rect x="0" y="0" width="1920" height="200" fill="#091936"/>
          <path d="M0 97L45.7 94.2C91.3 91.3 182.7 85.7 274.2 83C365.7 80.3 457.3 80.7 548.8 81.7C640.3 82.7 731.7 84.3 823 83.7C914.3 83 1005.7 80 1097 75.8C1188.3 71.7 1279.7 66.3 1371.2 70.3C1462.7 74.3 1554.3 87.7 1645.8 87C1737.3 86.3 1828.7 71.7 1874.3 64.3L1920 57L1920 201L1874.3 201C1828.7 201 1737.3 201 1645.8 201C1554.3 201 1462.7 201 1371.2 201C1279.7 201 1188.3 201 1097 201C1005.7 201 914.3 201 823 201C731.7 201 640.3 201 548.8 201C457.3 201 365.7 201 274.2 201C182.7 201 91.3 201 45.7 201L0 201Z" fill="#001e3d"/>
          <path d="M0 134L45.7 137.7C91.3 141.3 182.7 148.7 274.2 152C365.7 155.3 457.3 154.7 548.8 154.3C640.3 154 731.7 154 823 151.5C914.3 149 1005.7 144 1097 143.5C1188.3 143 1279.7 147 1371.2 147.5C1462.7 148 1554.3 145 1645.8 145.2C1737.3 145.3 1828.7 148.7 1874.3 150.3L1920 152L1920 201L1874.3 201C1828.7 201 1737.3 201 1645.8 201C1554.3 201 1462.7 201 1371.2 201C1279.7 201 1188.3 201 1097 201C1005.7 201 914.3 201 823 201C731.7 201 640.3 201 548.8 201C457.3 201 365.7 201 274.2 201C182.7 201 91.3 201 45.7 201L0 201Z" fill="#002244"/>
        </svg>
      </div>

      {/* Botão de voltar ao topo */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 bg-secondary text-primary p-3 rounded-full shadow-lg hover:bg-secondary-light transition-all duration-300 z-50 pointer-events-auto"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ position: 'fixed', bottom: '2rem', right: '2rem' }}
      >
        <FaArrowUp />
      </motion.button>

      <div className="container mx-auto pt-16">
        {/* Linha decorativa superior com efeito de brilho */}
        <div className="relative mb-12">
          <div className="w-24 h-1 bg-gradient-to-r from-secondary to-transparent"></div>
          <div className="absolute top-0 left-0 w-24 h-1 bg-secondary opacity-50 animate-pulse"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Logo e Sobre */}
          <div className="relative group">
            <div className="text-2xl font-bold mb-4 text-white flex items-center">
              <span className="w-8 h-8 bg-secondary/20 rounded-full flex items-center justify-center mr-3">
                <span className="w-4 h-4 bg-secondary rounded-full"></span>
              </span>
              RTL Energia
            </div>

            <div className="flex space-x-4">
              <motion.a
                href="https://instagram.com/rtl.energia"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary/10 p-2 rounded-full hover:bg-secondary hover:text-primary transition-all duration-300 border border-primary-light/20"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Instagram"
              >
                <FaInstagram />
              </motion.a>
              {/* Botão WhatsApp */}
              <motion.a
                href={`https://wa.me/55${getRandomWhatsappNumber()}?text=${encodeURIComponent('Olá! Gostaria de saber mais sobre a RTL Energia.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary/10 p-2 rounded-full hover:bg-secondary hover:text-primary transition-all duration-300 border border-primary-light/20"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="WhatsApp"
              >
                <FaWhatsapp />
              </motion.a>
            </div>
            <span className="w-2 h-2 bg-secondary/50 rounded-full mr-2 group-hover:bg-secondary transition-colors duration-300"></span>
                Joinville, SC
            {/* Linha decorativa lateral com efeito hover */}
            <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary-light/20 to-transparent group-hover:via-secondary transition-colors duration-300"></div>
          </div>

          {/* Links Rápidos */}
          <div className="relative group">
            <h3 className="text-lg font-bold mb-4 text-white flex items-center">
              <span className="w-8 h-px bg-secondary mr-3 group-hover:w-12 transition-all duration-300"></span>
              Links Rápidos
            </h3>
            <ul className="space-y-3">
              <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                <a href="#problema" className="text-gray-100 hover:text-secondary transition-colors flex items-center">
                  <span className="w-2 h-2 bg-secondary/50 rounded-full mr-2 group-hover:bg-secondary transition-colors duration-300"></span>
                  Como Funciona
                </a>
              </motion.li>
              <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                <a href="#depoimentos" className="text-gray-100 hover:text-secondary transition-colors flex items-center">
                  <span className="w-2 h-2 bg-secondary/50 rounded-full mr-2 group-hover:bg-secondary transition-colors duration-300"></span>
                  Depoimentos
                </a>
              </motion.li>
              <motion.li whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                <a href="#faq" className="text-gray-100 hover:text-secondary transition-colors flex items-center">
                  <span className="w-2 h-2 bg-secondary/50 rounded-full mr-2 group-hover:bg-secondary transition-colors duration-300"></span>
                  Perguntas Frequentes
                </a>
              </motion.li>
            </ul>
            {/* Linha decorativa lateral com efeito hover */}
            <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary-light/20 to-transparent group-hover:via-secondary transition-colors duration-300"></div>
          </div>

        

          
        </div>

        

        {/* Copyright */}
        <div className="border-t border-primary-light/10 pt-8 text-center text-gray-100">
          <p className="text-white">© {currentYear} RTL Energia. Todos os direitos reservados.</p>
          <div className="mt-2 text-sm">
            <a href="/politica-de-privacidade" className="hover:text-secondary transition-colors mr-4 text-gray-100">
              Política de Privacidade
            </a>
            <a href="/termos-de-uso" className="hover:text-secondary transition-colors text-gray-100">
              Termos de Uso
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 